import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ChevronRight,
  Inbox,
  Layers,
  Package,
  Plus,
  Settings,
  Shield,
  ShoppingBag,
  Star,
  Tag,
  TrendingUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { calcNetProfit, useApp, type Sale } from "@/context/AppContext";
import {
  playsetCardIds,
  playsetCandidatesFor,
  singlesCandidatesFor,
} from "@/features/listingEligibility";
import { getNearlyCompleteLines } from "@/features/pokemonFamilyMatcher";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/tools/recommendations")({
  component: () => (
    <ManagerOnly>
      <RecommendationsPage />
    </ManagerOnly>
  ),
});

type Priority = "high" | "medium" | "low";

interface CandidateRow {
  id: string;
  title: string;
  sub: string;
  actionLabel: string;
  run: () => void;
}

interface Recommendation {
  id: string;
  priority: Priority;
  category: string;
  Icon: LucideIcon;
  title: string;
  description: string;
  detail?: string;
  action?: { label: string; run: () => void };
  /** Per-card drill-down rows (used by the singles / playset listing engines). */
  candidates?: CandidateRow[];
}

function roiPct(costBasis: number, netRevenue: number): number {
  if (costBasis <= 0) return 0;
  return ((netRevenue - costBasis) / costBasis) * 100;
}

function fmt$(n: number, decimals = 0): string {
  return `$${Math.abs(n).toFixed(decimals)}`;
}

function RecommendationsPage() {
  const navigate = useNavigate();
  const { cards, sales, appPreferences } = useApp();

  const summary = useMemo(() => {
    const active = cards.filter(
      (c) => c.listingStatus !== "Sold" && c.quantity > 0,
    );
    const totalMarket = active.reduce(
      (s, c) => s + c.marketPrice * c.quantity,
      0,
    );
    const totalCost = active.reduce((s, c) => s + c.costBasis * c.quantity, 0);
    return {
      activeCount: active.length,
      totalMarket,
      potentialProfit: totalMarket - totalCost,
    };
  }, [cards]);

  const recs = useMemo<Recommendation[]>(() => {
    const result: Recommendation[] = [];

    const activeCards = cards.filter(
      (c) => c.listingStatus !== "Sold" && c.quantity > 0,
    );
    const unlistedCards = activeCards.filter(
      (c) => c.listingStatus === "Unlisted",
    );
    const soldCards = cards.filter((c) => c.listingStatus === "Sold");

    const salesByCardId = new Map<string, Sale[]>();
    sales.forEach((sale) => {
      if (sale.itemType === "Card") {
        const arr = salesByCardId.get(sale.itemId) ?? [];
        arr.push(sale);
        salesByCardId.set(sale.itemId, arr);
      }
    });

    // 1. Unlisted cards with profit potential
    const profitableUnlisted = unlistedCards
      .map((c) => ({ c, gap: (c.marketPrice - c.costBasis) * c.quantity }))
      .filter((x) => x.gap > 0)
      .sort((a, b) => b.gap - a.gap);
    if (profitableUnlisted.length > 0) {
      const totalGap = profitableUnlisted.reduce((s, x) => s + x.gap, 0);
      const top = profitableUnlisted[0].c;
      result.push({
        id: "unlisted-opportunity",
        priority: totalGap >= 50 ? "high" : "medium",
        category: "Action Required",
        Icon: Tag,
        title: `${profitableUnlisted.length} Unlisted Card${profitableUnlisted.length !== 1 ? "s" : ""} — ${fmt$(totalGap)} Waiting`,
        description: `You have ${fmt$(totalGap)} in potential profit sitting unlisted and unsold.`,
        detail: `Top pick: ${top.name} (${top.setName}) — est. ${fmt$(top.marketPrice - top.costBasis, 2)} profit per card at current market price.`,
        action: {
          label: "Go to Inventory",
          run: () => void navigate({ to: "/inventory" }),
        },
      });
    }

    // 2. Price alerts — market dropped below cost basis
    const underwater = activeCards.filter(
      (c) =>
        c.costBasis > 0 && c.marketPrice > 0 && c.marketPrice < c.costBasis,
    );
    if (underwater.length > 0) {
      const totalExposure = underwater.reduce(
        (s, c) => s + (c.costBasis - c.marketPrice) * c.quantity,
        0,
      );
      const worst = [...underwater].sort(
        (a, b) => b.costBasis - b.marketPrice - (a.costBasis - a.marketPrice),
      )[0];
      result.push({
        id: "price-alert",
        priority: "high",
        category: "Price Alert",
        Icon: AlertTriangle,
        title: `${underwater.length} Card${underwater.length !== 1 ? "s" : ""} Below Cost Basis`,
        description: `Market prices have dropped on ${underwater.length} card${underwater.length !== 1 ? "s" : ""}. Total loss exposure: ${fmt$(totalExposure, 2)}.`,
        detail: `Largest gap: ${worst.name} — paid ${fmt$(worst.costBasis, 2)}, now ${fmt$(worst.marketPrice, 2)}. Consider selling quickly or holding for recovery.`,
        action: {
          label: "Review Inventory",
          run: () => void navigate({ to: "/inventory" }),
        },
      });
    }

    // 3. Best sets to restock (from sold card history)
    if (soldCards.length > 0) {
      type SetStat = { totalROI: number; count: number; totalProfit: number };
      const setStats = new Map<string, SetStat>();
      soldCards.forEach((card) => {
        (salesByCardId.get(card.id) ?? []).forEach((sale) => {
          const net = calcNetProfit(sale);
          const roi = roiPct(card.costBasis, net);
          const prev = setStats.get(card.setName) ?? {
            totalROI: 0,
            count: 0,
            totalProfit: 0,
          };
          setStats.set(card.setName, {
            totalROI: prev.totalROI + roi,
            count: prev.count + 1,
            totalProfit: prev.totalProfit + (net - card.costBasis),
          });
        });
      });
      const topSets = Array.from(setStats.entries())
        .map(([name, s]) => ({
          name,
          avgROI: s.totalROI / s.count,
          totalProfit: s.totalProfit,
          count: s.count,
        }))
        .filter((s) => s.count >= 1)
        .sort((a, b) => b.avgROI - a.avgROI)
        .slice(0, 3);
      if (topSets.length > 0) {
        const top = topSets[0];
        const setList = topSets
          .map((s) => `${s.name} (${s.avgROI.toFixed(0)}% ROI)`)
          .join(" · ");
        result.push({
          id: "restock-sets",
          priority: "medium",
          category: "Restock Opportunity",
          Icon: TrendingUp,
          title: `Restock from ${top.name}`,
          description: `Your top-earning sets by ROI: ${setList}.`,
          detail: `Cards from ${top.name} averaged ${top.avgROI.toFixed(0)}% ROI across ${top.count} sale${top.count !== 1 ? "s" : ""} — sourcing more could replicate that return.`,
        });
      }
    }

    // 4. Best-performing rarities
    if (soldCards.length > 0) {
      type RarityStat = {
        totalROI: number;
        count: number;
        totalProfit: number;
      };
      const rarityStats = new Map<string, RarityStat>();
      soldCards.forEach((card) => {
        (salesByCardId.get(card.id) ?? []).forEach((sale) => {
          const net = calcNetProfit(sale);
          const roi = roiPct(card.costBasis, net);
          const prev = rarityStats.get(card.rarity) ?? {
            totalROI: 0,
            count: 0,
            totalProfit: 0,
          };
          rarityStats.set(card.rarity, {
            totalROI: prev.totalROI + roi,
            count: prev.count + 1,
            totalProfit: prev.totalProfit + (net - card.costBasis),
          });
        });
      });
      const topRarities = Array.from(rarityStats.entries())
        .map(([rarity, s]) => ({
          rarity,
          avgROI: s.totalROI / s.count,
          totalProfit: s.totalProfit,
          count: s.count,
        }))
        .sort((a, b) => b.avgROI - a.avgROI)
        .slice(0, 3);
      if (topRarities.length > 0) {
        const top = topRarities[0];
        const rarityList = topRarities
          .map((r) => `${r.rarity} (${r.avgROI.toFixed(0)}%)`)
          .join(" · ");
        result.push({
          id: "best-rarities",
          priority: "low",
          category: "Sourcing Insight",
          Icon: Star,
          title: `Prioritise ${top.rarity} Cards When Buying`,
          description: `Best ROI by rarity from your sales history: ${rarityList}.`,
          detail: `When evaluating lots, weight heavily toward ${top.rarity} cards — they've consistently delivered your highest returns.`,
        });
      }
    }

    // 4b. Nearly-complete Pokémon family lines
    const nearly = getNearlyCompleteLines(unlistedCards, 0.5).slice(0, 2);
    if (nearly.length > 0) {
      const top = nearly[0];
      const missing = top.missingPokemon.slice(0, 3).join(", ");
      const moreMissing =
        top.missingPokemon.length > 3
          ? ` and ${top.missingPokemon.length - 3} more`
          : "";
      const otherLine = nearly[1]
        ? ` Also close: ${nearly[1].family.label} (${Math.round(nearly[1].completionPercent * 100)}% complete).`
        : "";
      const detail =
        top.missingPokemon.length === 1
          ? `You're 1 ${missing} away from a ${top.family.label} bundle.${otherLine}`
          : `Missing: ${missing}${moreMissing}.${otherLine}`;
      result.push({
        id: "nearly-complete-family",
        priority: "medium",
        category: "Bundle Opportunity",
        Icon: Package,
        title: `${top.family.label} — ${Math.round(top.completionPercent * 100)}% Complete`,
        description: `You already have ${top.presentPokemon.length} of ${top.presentPokemon.length + top.missingPokemon.length} cards in this line. Close the gap or bundle what you have.`,
        detail,
        action: {
          label: "Find missing cards",
          run: () => void navigate({ to: "/inventory" }),
        },
      });
    }

    // 5. High-value cards in active inventory
    const highValue = [...activeCards]
      .filter((c) => c.marketPrice >= 10)
      .sort((a, b) => b.marketPrice * b.quantity - a.marketPrice * a.quantity)
      .slice(0, 5);
    if (highValue.length > 0) {
      const totalValue = highValue.reduce(
        (s, c) => s + c.marketPrice * c.quantity,
        0,
      );
      const names = highValue
        .slice(0, 3)
        .map((c) => c.name)
        .join(", ");
      result.push({
        id: "high-value-watch",
        priority: "low",
        category: "Inventory Watch",
        Icon: Shield,
        title: `${fmt$(totalValue)} in High-Value Cards`,
        description: `Top cards by value: ${names}${highValue.length > 3 ? ` and ${highValue.length - 3} more` : ""}.`,
        detail:
          "Ensure these are stored safely, insured if shipping, and priced at current market rates.",
        action: {
          label: "View Inventory",
          run: () => void navigate({ to: "/inventory" }),
        },
      });
    }

    // 6. Venue performance
    if (sales.length >= 3) {
      type VenueStat = { totalNet: number; count: number };
      const venueStats = new Map<string, VenueStat>();
      sales.forEach((sale) => {
        const net = calcNetProfit(sale);
        const prev = venueStats.get(sale.venue) ?? { totalNet: 0, count: 0 };
        venueStats.set(sale.venue, {
          totalNet: prev.totalNet + net,
          count: prev.count + 1,
        });
      });
      const venues = Array.from(venueStats.entries())
        .map(([venue, s]) => ({
          venue,
          avgNet: s.totalNet / s.count,
          count: s.count,
        }))
        .sort((a, b) => b.avgNet - a.avgNet);
      if (venues.length >= 2) {
        const top = venues[0];
        const details = venues
          .map((v) => `${v.venue}: avg ${fmt$(v.avgNet, 2)}/sale (${v.count})`)
          .join(" · ");
        result.push({
          id: "venue-insight",
          priority: "low",
          category: "Sales Insight",
          Icon: ShoppingBag,
          title: `${top.venue} Is Your Best Venue`,
          description: `${top.venue} averages ${fmt$(top.avgNet, 2)} net per sale after all fees — your most profitable channel.`,
          detail: details,
          action: {
            label: "View Sales",
            run: () => void navigate({ to: "/sales" }),
          },
        });
      }
    }

    // 7. Cost-range sourcing insight
    if (soldCards.length >= 5) {
      type RangeStat = { totalROI: number; count: number };
      const ranges: Array<{
        label: string;
        min: number;
        max: number;
        stat: RangeStat;
      }> = [
        { label: "Under $1", min: 0, max: 1, stat: { totalROI: 0, count: 0 } },
        { label: "$1–$5", min: 1, max: 5, stat: { totalROI: 0, count: 0 } },
        { label: "$5–$20", min: 5, max: 20, stat: { totalROI: 0, count: 0 } },
        {
          label: "$20+",
          min: 20,
          max: Infinity,
          stat: { totalROI: 0, count: 0 },
        },
      ];
      soldCards.forEach((card) => {
        (salesByCardId.get(card.id) ?? []).forEach((sale) => {
          const range = ranges.find(
            (r) => card.costBasis >= r.min && card.costBasis < r.max,
          );
          if (!range) return;
          range.stat.totalROI += roiPct(card.costBasis, calcNetProfit(sale));
          range.stat.count += 1;
        });
      });
      const withData = ranges
        .filter((r) => r.stat.count >= 2)
        .sort(
          (a, b) =>
            b.stat.totalROI / b.stat.count - a.stat.totalROI / a.stat.count,
        );
      if (withData.length >= 2) {
        const best = withData[0];
        const avgROI = best.stat.totalROI / best.stat.count;
        const rangeDetails = withData
          .map(
            (r) =>
              `${r.label}: ${(r.stat.totalROI / r.stat.count).toFixed(0)}% avg ROI`,
          )
          .join(" · ");
        result.push({
          id: "cost-range",
          priority: "low",
          category: "Buying Strategy",
          Icon: Settings,
          title: `Sweet Spot: ${best.label} Cards`,
          description: `Cards costing ${best.label} have averaged ${avgROI.toFixed(0)}% ROI in your history.`,
          detail: rangeDetails,
        });
      }
    }

    // 8. Single-card listing candidates — unlisted cards over the bulk threshold
    const threshold = appPreferences.bulkThresholdUsd;
    const singles = singlesCandidatesFor(cards, threshold);
    if (singles.length > 0) {
      const totalValue = singles.reduce(
        (s, c) => s + c.marketPrice * c.quantity,
        0,
      );
      const top = singles[0];
      result.push({
        id: "single-card-candidates",
        priority: totalValue >= 25 ? "high" : "medium",
        category: "Action Required",
        Icon: Star,
        title: `${singles.length} Card${singles.length !== 1 ? "s" : ""} Worth Listing Solo — ${fmt$(totalValue)} Potential`,
        description: `You have ${singles.length} unlisted card${singles.length !== 1 ? "s" : ""} over the ${fmt$(threshold, 2)} bulk threshold totaling ${fmt$(totalValue)} in market value.`,
        detail: `Top pick: ${top.name}${top.setName ? ` (${top.setName})` : ""} — ${fmt$(top.marketPrice, 2)}/each, ${top.quantity} cop${top.quantity !== 1 ? "ies" : "y"}.`,
        candidates: singles.slice(0, 25).map((c) => ({
          id: c.id,
          title: `${c.name}${c.setName ? ` · ${c.setName}` : ""}`,
          sub: `${fmt$(c.marketPrice, 2)}/each · ${c.condition} · ${c.quantity} cop${c.quantity !== 1 ? "ies" : "y"}`,
          actionLabel: "List solo",
          run: () =>
            void navigate({
              to: "/products/create",
              search: {
                initialCardIds: c.id,
                initialProductType: "Premium Single",
              },
            }),
        })),
      });
    }

    // 9. Playset candidates — ≥ minQty unlisted copies of one identity
    const minQty = appPreferences.playsetMinQty;
    const playsets = playsetCandidatesFor(cards, minQty);
    if (playsets.length > 0) {
      const totalValue = playsets.reduce(
        (s, p) => s + p.marketPrice * minQty,
        0,
      );
      const top = playsets[0];
      result.push({
        id: "playset-candidates",
        priority: "medium",
        category: "Opportunity",
        Icon: Layers,
        title: `${playsets.length} Playset${playsets.length !== 1 ? "s" : ""} Ready to Build — ${fmt$(totalValue)} Potential`,
        description: `You have ≥${minQty} copies of ${playsets.length} different card${playsets.length !== 1 ? "s" : ""}. Playsets sell faster than singles for tournament demand.`,
        detail: `Top pick: ${top.name} — ${top.totalQty} copies, ${fmt$(top.marketPrice, 2)} each = ${fmt$(top.marketPrice * minQty, 2)} playset.`,
        candidates: playsets.map((p) => ({
          id: p.key,
          title: `${p.name}${p.setName ? ` · ${p.setName}` : ""}`,
          sub: `${p.totalQty} copies · ${p.condition} · ${fmt$(p.marketPrice, 2)} each = ${fmt$(p.marketPrice * minQty, 2)} playset`,
          actionLabel: "Build playset",
          run: () =>
            void navigate({
              to: "/products/create",
              search: {
                initialCardIds: playsetCardIds(p, minQty).join(","),
                initialProductType: "4x Playset",
              },
            }),
        })),
      });
    }

    return result;
  }, [cards, sales, appPreferences, navigate]);

  const highCount = recs.filter((r) => r.priority === "high").length;
  const priorityLabel = (p: Priority) =>
    p === "high" ? "Action" : p === "medium" ? "Opportunity" : "Insight";
  const priorityChip = (p: Priority) =>
    p === "high"
      ? "bg-destructive/15 text-destructive"
      : p === "medium"
        ? "bg-secondary/20 text-secondary-foreground"
        : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
  const priorityRing = (p: Priority) =>
    p === "high" ? "border-destructive/40" : "border-border";
  const iconTint = (p: Priority) =>
    p === "high"
      ? "bg-destructive/15 text-destructive"
      : p === "medium"
        ? "bg-secondary/20 text-secondary-foreground"
        : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";

  return (
    <div className="space-y-3">
      <BackButton fallback={{ to: "/tools" }} />
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold text-foreground">Recommendations</h1>
        {highCount > 0 && (
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold text-destructive-foreground">
            {highCount}
          </span>
        )}
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-3 divide-x divide-primary-foreground/20 rounded-md bg-primary py-4 text-primary-foreground">
        <SummaryStat
          label="Active Cards"
          value={summary.activeCount.toLocaleString()}
        />
        <SummaryStat label="Market Value" value={fmt$(summary.totalMarket)} />
        <SummaryStat
          label="vs Cost Basis"
          value={`${summary.potentialProfit >= 0 ? "+" : "-"}${fmt$(summary.potentialProfit)}`}
          valueClass={
            summary.potentialProfit >= 0 ? "text-green-400" : "text-red-400"
          }
        />
      </div>

      {recs.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">
            No Recommendations Yet
          </h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Add cards to your inventory and log some sales — the engine will
            analyse your data and surface actionable buying, listing, and
            pricing insights here.
          </p>
          <button
            type="button"
            onClick={() => void navigate({ to: "/inventory/bulk" })}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            Add Cards
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {recs.length} recommendation{recs.length !== 1 ? "s" : ""} based on
            your current inventory
          </p>
          <div className="space-y-3">
            {recs.map((rec) => (
              <article
                key={rec.id}
                className={`space-y-2 rounded-md border bg-card p-3.5 ${priorityRing(rec.priority)}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${iconTint(rec.priority)}`}
                  >
                    <rec.Icon className="h-[18px] w-[18px]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${priorityChip(rec.priority)}`}
                      >
                        {priorityLabel(rec.priority)}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {rec.category}
                      </span>
                    </div>
                    <h3 className="text-sm font-bold text-foreground">
                      {rec.title}
                    </h3>
                  </div>
                </div>

                <p className="text-[13px] leading-5 text-muted-foreground">
                  {rec.description}
                </p>

                {rec.detail && (
                  <div className="rounded-md bg-muted px-3 py-2 text-xs leading-5 text-foreground">
                    {rec.detail}
                  </div>
                )}

                {rec.candidates && rec.candidates.length > 0 && (
                  <details className="rounded-md border border-border">
                    <summary className="cursor-pointer list-none px-3 py-2 text-xs font-semibold text-primary hover:bg-muted">
                      Review {rec.candidates.length} candidate
                      {rec.candidates.length !== 1 ? "s" : ""}
                    </summary>
                    <ul className="divide-y divide-border border-t border-border">
                      {rec.candidates.map((cand) => (
                        <li
                          key={cand.id}
                          className="flex items-center gap-2 px-3 py-2"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[13px] font-medium text-foreground">
                              {cand.title}
                            </div>
                            <div className="truncate text-[11px] text-muted-foreground">
                              {cand.sub}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={cand.run}
                            className="shrink-0 rounded-md bg-primary px-2.5 py-1.5 text-[11px] font-semibold text-primary-foreground hover:opacity-90"
                          >
                            {cand.actionLabel}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {rec.action && (
                  <button
                    type="button"
                    onClick={() => rec.action?.run()}
                    className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-xs font-semibold text-primary hover:bg-muted"
                  >
                    {rec.action.label}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryStat({
  label,
  value,
  valueClass = "",
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex flex-col items-center">
      <span className={`text-xl font-bold ${valueClass}`}>{value}</span>
      <span className="text-[11px] text-primary-foreground/70">{label}</span>
    </div>
  );
}
